import { useEffect, useMemo, useRef, useState } from 'react';
import { getSettings, putSetting, testTonConfig, uploadBrandIcon, clearBrandIcon, type AdminSetting, type TonConfigTest } from '../api';
import { useApi } from '../useApi';

/**
 * Dedicated payments config — all payment settings in one place. Everything flows
 * through the generic Settings API (GET /v1/admin/settings, PUT …/:key); there are
 * no payment-specific endpoints. The Toncenter API key is a masked secret (reuses
 * the Settings "replace" pattern). Switching network/wallet here takes effect with
 * no redeploy; the TON Connect manifest is generated per-domain automatically.
 */

const K_NETWORK = 'payments.ton_network';
const K_ADDRESS = 'payments.ton_address';
const K_TONCENTER = 'payments.toncenter_base';
const K_TONCENTER_KEY = 'payments.toncenter_api_key';
const K_BOOST_TON = 'boost.price_ton';
const K_BOOST_STARS = 'boost.price_stars';
const K_ICON_URL = 'payments.manifest_icon_url';

const valueOf = (s: AdminSetting | undefined): string => s?.value ?? '';

export function PaymentSettings() {
  const { data, error, loading, reload } = useApi(getSettings);
  const rows = useMemo(() => data?.data ?? [], [data]);
  const byKey = useMemo(() => {
    const m = new Map<string, AdminSetting>();
    for (const r of rows) m.set(r.key, r);
    return m;
  }, [rows]);

  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // drafts
  const [network, setNetwork] = useState('testnet');
  const [address, setAddress] = useState('');
  const [toncenter, setToncenter] = useState('');
  const [boostTon, setBoostTon] = useState('');
  const [boostStars, setBoostStars] = useState('');
  const [apiKey, setApiKey] = useState(''); // secret "replace" field; '' = leave as-is

  // live config test
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<TonConfigTest | null>(null);

  // wallet-connect (manifest) icon uploader
  const [iconSize, setIconSize] = useState('180');
  const [iconBusy, setIconBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const iconUrl = valueOf(byKey.get(K_ICON_URL));

  const onPickIcon = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setIconBusy(true);
    setActionError(null);
    try {
      const px = Math.min(512, Math.max(64, Math.floor(Number(iconSize) || 180)));
      await uploadBrandIcon(file, px);
      reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Icon upload failed.');
    } finally {
      setIconBusy(false);
    }
  };

  const removeIcon = async () => {
    setIconBusy(true);
    setActionError(null);
    try {
      await clearBrandIcon();
      reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Remove failed.');
    } finally {
      setIconBusy(false);
    }
  };

  useEffect(() => {
    if (!data) return;
    const raw = valueOf(byKey.get(K_NETWORK));
    setNetwork(raw === 'mainnet' ? 'mainnet' : 'testnet');
    setAddress(valueOf(byKey.get(K_ADDRESS)));
    setToncenter(valueOf(byKey.get(K_TONCENTER)));
    setBoostTon(valueOf(byKey.get(K_BOOST_TON)));
    setBoostStars(valueOf(byKey.get(K_BOOST_STARS)));
    setApiKey('');
    setTest(null); // saved config changed → old result is stale
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reseed only on fetched data
  }, [data]);

  const runTest = async () => {
    setTesting(true);
    setTest(null);
    setActionError(null);
    try {
      setTest(await testTonConfig());
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Test failed.');
    } finally {
      setTesting(false);
    }
  };

  const save = async (key: string, value: string, opts?: { isSecret?: boolean; reloadAfter?: boolean }) => {
    setBusyKey(key);
    setActionError(null);
    setSavedKey(null);
    try {
      await putSetting(key, value, opts?.isSecret);
      setSavedKey(key);
      if (opts?.reloadAfter) reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setBusyKey(null);
    }
  };

  const saveLabel = (key: string, label = 'Save') =>
    busyKey === key ? 'Saving…' : savedKey === key ? 'Saved ✓' : label;

  const apiKeyHint = byKey.get(K_TONCENTER_KEY)?.hint;
  const liveNetwork = valueOf(byKey.get(K_NETWORK)) || 'testnet';
  const liveAddress = valueOf(byKey.get(K_ADDRESS));

  return (
    <div className="admin-wrap">
      <div className="page-head">
        <h1>Payments<span className="dot">.</span></h1>
        <span className={`badge ${liveNetwork === 'mainnet' ? 'badge-ok' : 'badge-warn'}`}>{liveNetwork}</span>
      </div>
      <p className="hint">
        Non-custodial rails: <strong>Telegram Stars</strong> (works out of the box via the bot) and{' '}
        <strong>TON Connect</strong>. To accept real TON, set the network to <code>mainnet</code> and your
        mainnet wallet below — no redeploy. The TON Connect manifest is generated per-domain automatically.
      </p>
      {!liveAddress && (
        <p className="error-line">TON payments are inactive — set a receiving wallet below.</p>
      )}
      {actionError && <p className="error-line">{actionError}</p>}

      {error ? (
        <div className="card"><div className="empty">{error}</div></div>
      ) : loading && !data ? (
        <div className="card"><div className="empty">Loading…</div></div>
      ) : (
        <>
          {/* ---------- TON ---------- */}
          <section className="card">
            <div className="card-head"><span className="card-title">TON</span></div>
            <div className="form-grid">
              <div className="field">
                <label>Network</label>
                <select className="input" value={network} onChange={(e) => setNetwork(e.target.value)}>
                  <option value="testnet">Testnet (testing)</option>
                  <option value="mainnet">Mainnet (real funds)</option>
                </select>
                <button className="btn btn-primary" disabled={busyKey === K_NETWORK} onClick={() => void save(K_NETWORK, network, { reloadAfter: true })} style={{ marginTop: 8 }}>
                  {saveLabel(K_NETWORK, 'Save network')}
                </button>
              </div>
              <div className="field">
                <label>Receiving wallet</label>
                <input className="input" placeholder="UQ… / EQ… (mainnet) or 0Q… (testnet)" value={address} onChange={(e) => setAddress(e.target.value)} />
                <button className="btn btn-primary" disabled={busyKey === K_ADDRESS} onClick={() => void save(K_ADDRESS, address.trim(), { reloadAfter: true })} style={{ marginTop: 8 }}>
                  {saveLabel(K_ADDRESS, 'Save wallet')}
                </button>
              </div>
            </div>

            <div className="field-label" style={{ marginTop: 18 }}>Indexer (Toncenter) <span className="hint" style={{ textTransform: 'none', letterSpacing: 0 }}>— optional; blank auto-derives from network</span></div>
            <div className="form-grid" style={{ marginTop: 8 }}>
              <div className="field">
                <label>Base URL</label>
                <input className="input" placeholder="blank = auto (mainnet/testnet toncenter.com)" value={toncenter} onChange={(e) => setToncenter(e.target.value)} />
                <button className="btn" disabled={busyKey === K_TONCENTER} onClick={() => void save(K_TONCENTER, toncenter.trim())} style={{ marginTop: 8 }}>
                  {saveLabel(K_TONCENTER)}
                </button>
              </div>
              <div className="field">
                <label>API key <span className="hint" style={{ textTransform: 'none', letterSpacing: 0 }}>(secret · higher rate limits)</span></label>
                <span className="muted mono" style={{ fontSize: '0.78rem' }}>{apiKeyHint ?? '(not set)'}</span>
                <input className="input" type="password" placeholder={apiKeyHint ? 'Replace key…' : 'Paste Toncenter key…'} value={apiKey} onChange={(e) => setApiKey(e.target.value)} style={{ marginTop: 6 }} />
                <button className="btn" disabled={busyKey === K_TONCENTER_KEY || apiKey === ''} onClick={() => void save(K_TONCENTER_KEY, apiKey, { isSecret: true, reloadAfter: true }).then(() => setApiKey(''))} style={{ marginTop: 8 }}>
                  {saveLabel(K_TONCENTER_KEY, 'Replace key')}
                </button>
              </div>
            </div>

            {/* live config test — validates the SAVED config (wallet + network + indexer). */}
            <div className="field-label" style={{ marginTop: 18 }}>Test configuration</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 560, marginTop: 8 }}>
              <button className="btn" disabled={testing} onClick={() => void runTest()} style={{ alignSelf: 'flex-start' }}>
                {testing ? 'Testing…' : 'Test TON config'}
              </button>
              <span className="hint">Checks the <strong>saved</strong> wallet parses, matches the network, and that the indexer is reachable. Save your changes first.</span>
              {test && (
                <div style={{ borderLeft: `3px solid ${test.ok ? '#3fb950' : '#e5484d'}`, paddingLeft: 10, marginTop: 2 }}>
                  <strong style={{ color: test.ok ? '#3fb950' : '#e5484d' }}>{test.ok ? '✓ Working' : '✗ Issue'}</strong>
                  <p style={{ margin: '4px 0', fontSize: '0.84rem' }}>{test.message}</p>
                  <span className="muted mono" style={{ fontSize: '0.74rem' }}>
                    {[
                      `network: ${test.network}`,
                      `wallet: ${test.addressValid ? 'valid' : test.address ? 'invalid' : 'unset'}`,
                      `indexer: ${test.indexerOk ? 'ok' : test.indexerStatus ? `HTTP ${test.indexerStatus}` : 'unreachable'}`,
                    ].join(' · ')}
                  </span>
                </div>
              )}
            </div>
          </section>

          {/* ---------- Wallet-connect icon ---------- */}
          <section className="card">
            <div className="card-head"><span className="card-title">Wallet-connect icon</span></div>
            <p className="hint" style={{ marginBottom: 12 }}>
              The icon shown in the TON wallet's "Connect to …" dialog. Upload a square image — it's
              resized to <strong>{iconSize}×{iconSize}</strong> PNG. Wallets may cache it for a few minutes.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <span style={{ width: 72, height: 72, borderRadius: 16, background: 'var(--surface-2)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flex: 'none' }}>
                {iconUrl ? <img src={iconUrl} alt="icon" width={72} height={72} style={{ objectFit: 'cover' }} /> : <span className="muted" style={{ fontSize: '0.7rem' }}>default</span>}
              </span>
              <div className="field" style={{ width: 110 }}>
                <label>Size (px)</label>
                <input className="input" value={iconSize} onChange={(e) => setIconSize(e.target.value)} />
              </div>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} onChange={onPickIcon} />
              <div className="row-actions">
                <button className="btn btn-primary" disabled={iconBusy} onClick={() => fileRef.current?.click()}>
                  {iconBusy ? 'Uploading…' : iconUrl ? 'Replace icon' : 'Upload icon'}
                </button>
                {iconUrl ? (
                  <button className="btn btn-danger" disabled={iconBusy} onClick={() => void removeIcon()}>Remove</button>
                ) : null}
              </div>
            </div>
          </section>

          {/* ---------- Boost pricing ---------- */}
          <section className="card">
            <div className="card-head"><span className="card-title">One-off Boost pricing</span></div>
            <div className="form-grid">
              <div className="field">
                <label>Price · TON</label>
                <input className="input" value={boostTon} onChange={(e) => setBoostTon(e.target.value)} />
                <button className="btn btn-primary" disabled={busyKey === K_BOOST_TON} onClick={() => void save(K_BOOST_TON, boostTon.trim())} style={{ marginTop: 8 }}>
                  {saveLabel(K_BOOST_TON)}
                </button>
              </div>
              <div className="field">
                <label>Price · Stars</label>
                <input className="input" value={boostStars} onChange={(e) => setBoostStars(e.target.value)} />
                <button className="btn btn-primary" disabled={busyKey === K_BOOST_STARS} onClick={() => void save(K_BOOST_STARS, boostStars.trim())} style={{ marginTop: 8 }}>
                  {saveLabel(K_BOOST_STARS)}
                </button>
              </div>
            </div>
            <p className="hint" style={{ marginTop: 12 }}>
              Telegram Stars needs no wallet config — it settles through the bot. Plan prices live under{' '}
              <a href="/plans" style={{ color: 'var(--accent)' }}>Plans</a>.
            </p>
          </section>
        </>
      )}
    </div>
  );
}
